// Scrap Quote Generator — shared two-step form/result UI.
//
// Used by:
//   - QuotePage.tsx — standalone /quote route, wrapped in PageHeader+Card
//   - components/ui/QuoteModal/QuoteModal.tsx — same content inside Modal
//
// Step 1 (form): vehicle reg + optional customer + price per tonne
//   (pre-filled from company default, editable inline as an override).
// Step 2 (result): make/model/weight/price/value + send-by-email + new-quote.
//
// Weight source is surfaced via a Badge for verified-data quotes only:
//   seed → green "From database"
//   dvla → blue  "DVLA verified" (reserved — DVLA does not return weight
//                                 today, but the badge is wired so the
//                                 server can promote a quote without a UI
//                                 change)
// If the vehicle cannot be identified from real data (no DVLA/MOT match
// or no seed weight), the server returns a not-found response and we
// render a clear "Vehicle not found" message with no value.

import { useEffect, useState, type FormEvent } from 'react';
import { Calculator, Mail, RotateCcw } from 'lucide-react';

import { Badge, type BadgeVariant } from '../../components/ui/Badge/Badge';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { useToast } from '../../hooks/useToast';
import { generateQuote, sendQuote } from '../../api/quotes';
import { useCollectionsSettings } from '../collections/settings/useCollectionsSettings';
import type {
  GenerateQuoteInput,
  QuoteResponse,
  WeightSource,
} from '../../types/api';
import styles from './QuotePage.module.css';

const WEIGHT_SOURCE_VARIANT: Record<WeightSource, BadgeVariant> = {
  seed: 'success',
  dvla: 'info',
};

const WEIGHT_SOURCE_LABEL: Record<WeightSource, string> = {
  seed: 'From database',
  dvla: 'DVLA verified',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(n);
}

function formatTonnes(kg: number): string {
  return (kg / 1000).toFixed(3);
}

function extractMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const r = (err as { response?: { data?: { message?: string } } }).response;
    if (r?.data?.message) return r.data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export interface QuoteFlowProps {
  /** Called after a successful send — used by QuoteModal to auto-close. */
  onSent?: () => void;
}

export function QuoteFlow({ onSent }: QuoteFlowProps): JSX.Element {
  const collectionsSettings = useCollectionsSettings();
  const companyDefaultPrice =
    collectionsSettings.data?.scrap_price_per_tonne ?? null;
  const toast = useToast();

  const [vehicleReg, setVehicleReg] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [priceInput, setPriceInput] = useState<string>('');
  const [priceDirty, setPriceDirty] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [sending, setSending] = useState(false);

  // Pre-fill price from the company default once it loads; the dirty flag
  // protects user edits from being overwritten on a refetch.
  useEffect(() => {
    if (!priceDirty && priceInput === '' && companyDefaultPrice !== null) {
      setPriceInput(String(companyDefaultPrice));
    }
  }, [companyDefaultPrice, priceDirty, priceInput]);

  async function handleGenerate(e: FormEvent): Promise<void> {
    e.preventDefault();
    const reg = vehicleReg.trim().toUpperCase();
    if (reg.length === 0) {
      setError('Vehicle registration is required');
      return;
    }
    const priceTrimmed = priceInput.trim();
    const priceOverride = priceTrimmed.length > 0 ? Number(priceTrimmed) : null;
    if (priceOverride !== null && (!Number.isFinite(priceOverride) || priceOverride <= 0)) {
      setError('Price per tonne must be a positive number');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const payload: GenerateQuoteInput = { vehicle_reg: reg };
      if (customerName.trim()) payload.customer_name = customerName.trim();
      if (customerEmail.trim()) payload.customer_email = customerEmail.trim();
      if (priceOverride !== null) payload.price_per_tonne_override = priceOverride;
      const result = await generateQuote(payload);
      setQuote(result);
    } catch (err) {
      setError(extractMessage(err, 'Failed to generate quote'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSend(): Promise<void> {
    if (!quote || !quote.found) return;
    setSending(true);
    try {
      const r = await sendQuote(quote.uuid);
      if (r.email_queued) {
        toast.success(
          customerEmail
            ? `Quote sent to ${customerEmail}`
            : 'Quote sent',
        );
        onSent?.();
      } else {
        toast.warning('Quote not sent — email template not configured');
      }
    } catch (err) {
      toast.error(extractMessage(err, 'Failed to send quote'));
    } finally {
      setSending(false);
    }
  }

  function handleNewQuote(): void {
    setQuote(null);
    setVehicleReg('');
    setCustomerName('');
    setCustomerEmail('');
    setError(null);
    setPriceDirty(false);
    setPriceInput(companyDefaultPrice !== null ? String(companyDefaultPrice) : '');
  }

  // -------------------------------------------------------- RESULT (not found)
  if (quote && !quote.found) {
    return (
      <div className={styles.flow}>
        <h3 className={styles.resultRegHead}>Quote for {quote.vehicle_reg}</h3>
        <p className={styles.resultNote}>
          Vehicle could not be identified. We could not confirm the make,
          model, or weight from DVLA or our database, so no quote can be
          generated.
        </p>
        <div className={styles.actionsRow}>
          <Button
            variant="secondary"
            icon={RotateCcw}
            onClick={handleNewQuote}
          >
            New Quote
          </Button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ RESULT (found)
  if (quote) {
    const { make, model, weight_source, weight_kg, price_per_tonne, scrap_value } = quote;
    const hasMake = typeof make === 'string' && make.length > 0;
    const hasModel = typeof model === 'string' && model.length > 0;
    const vehicleLabel = hasMake && hasModel
      ? `${make} ${model}`
      : hasMake
        ? `Make: ${make}`
        : 'Unknown';
    const canSend = customerEmail.trim().length > 0;

    return (
      <div className={styles.flow}>
        <h3 className={styles.resultRegHead}>Quote for {quote.vehicle_reg}</h3>

        <dl className={styles.results}>
          <div className={styles.resultRow}>
            <dt>Vehicle</dt>
            <dd>{vehicleLabel}</dd>
          </div>
          <div className={styles.resultRow}>
            <dt>Weight used</dt>
            <dd>
              {formatTonnes(weight_kg)} tonnes
              <Badge
                variant={WEIGHT_SOURCE_VARIANT[weight_source]}
                className={styles.weightBadge}
              >
                {WEIGHT_SOURCE_LABEL[weight_source]}
              </Badge>
            </dd>
          </div>
          <div className={styles.resultRow}>
            <dt>Price per tonne</dt>
            <dd>{formatMoney(price_per_tonne)}</dd>
          </div>
        </dl>

        <div className={styles.scrapValue}>
          <span className={styles.scrapValueLabel}>Scrap value</span>
          <span className={styles.scrapValueAmount}>{formatMoney(scrap_value)}</span>
        </div>

        <p className={styles.validity}>
          This quote is valid for <strong>48 hours</strong> from now.
        </p>

        <div className={styles.actionsRow}>
          <Button
            variant="primary"
            icon={Mail}
            onClick={handleSend}
            loading={sending}
            disabled={!canSend || sending}
          >
            Send Quote by Email
          </Button>
          <Button
            variant="secondary"
            icon={RotateCcw}
            onClick={handleNewQuote}
            disabled={sending}
          >
            New Quote
          </Button>
        </div>
        {!canSend ? (
          <p className={styles.muted}>
            Add a customer email above and generate the quote again to enable sending.
          </p>
        ) : null}
      </div>
    );
  }

  // ----------------------------------------------------------------- FORM
  return (
    <form onSubmit={handleGenerate} className={styles.flow}>
      <Input
        label="Vehicle registration"
        value={vehicleReg}
        onChange={(e) => setVehicleReg(e.target.value)}
        onBlur={(e) => setVehicleReg(e.target.value.toUpperCase())}
        placeholder="e.g. AB12 XYZ"
        required
        autoFocus
      />
      <div className={styles.row}>
        <Input
          label="Customer name (optional)"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
        />
        <Input
          label="Customer email (required to send)"
          variant="email"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
        />
      </div>
      <Input
        label="Price per tonne (£/t)"
        variant="number"
        min={0}
        step="0.01"
        value={priceInput}
        onChange={(e) => {
          setPriceInput(e.target.value);
          setPriceDirty(true);
        }}
        helper={
          companyDefaultPrice !== null
            ? `Pre-filled from your company default. Adjust the rate inline to override for this quote only.`
            : `No company default set. Enter a rate for this quote, or configure a default in Settings → Company.`
        }
      />
      {error ? <p className={styles.error}>{error}</p> : null}
      <div className={styles.actionsRow}>
        <Button
          variant="primary"
          icon={Calculator}
          type="submit"
          loading={loading}
          disabled={loading}
        >
          Get Quote
        </Button>
      </div>
    </form>
  );
}
